"use client";

import Image from "next/image";
import { useState, type ReactNode } from "react";
import type { PublicClientPortal } from "../lib/clientPortalApi";
import { initials, safePortalImageUrl, safeWebUrl } from "../lib/portalPresentation";
import { usePortalTranslations } from "../i18n/client";
import { PortalReviewSummary } from "./PortalReviewSummary";
import styles from "./PortalHero.module.css";

type PortalHeroProps = {
  portal: PublicClientPortal;
  account: ReactNode;
  expanded: boolean;
  hasNavigation: boolean;
  hasBooking: boolean;
  hasEvents: boolean;
  hasReviews: boolean;
};

export function PortalHero({ portal, account, expanded, hasNavigation, hasBooking, hasEvents, hasReviews }: PortalHeroProps) {
  const t = usePortalTranslations();
  const base = `/${encodeURIComponent(portal.slug)}`;
  const presentation = portal.presentation;
  const logo = safePortalImageUrl(presentation?.logoUrl);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const visibleLogo = logo !== failedLogo ? logo : null;
  const photo = safePortalImageUrl(presentation?.heroImageUrl);
  const map = safeWebUrl(presentation?.mapUrl);
  const imagePosition = { objectPosition: `${presentation?.heroImagePositionX ?? 50}% ${presentation?.heroImagePositionY ?? 50}%` };
  const description = presentation?.description || t(hasBooking
    ? hasEvents ? "portal.home.bookingAndEventsDescription" : "portal.home.bookingDescription"
    : hasEvents ? "portal.home.eventsDescription" : "common.description");

  return <header className={`${styles.header} ${expanded ? "" : styles.compactHeader} ${hasNavigation ? styles.tabbedHeader : ""}`} data-portal-hero>
    <div className={styles.topbar} data-portal-topbar>
      <div className={styles.controls}>
        <a className={`${styles.brand} ${visibleLogo ? "" : styles.defaultBrand}`} href={base} aria-label={`${portal.name} — ${t("portal.navigation.backToOverview")}`}>
          {visibleLogo ? <Image key={visibleLogo} src={visibleLogo} alt={portal.name} width={180} height={44} unoptimized priority onError={() => setFailedLogo(visibleLogo)} />
            : <span aria-hidden="true">{initials(portal.name)}</span>}
        </a>
        <div className={styles.account} data-portal-account>{account}</div>
      </div>
    </div>
    <div className={`${styles.hero} ${expanded ? "" : styles.compact} ${hasNavigation ? styles.withNavigation : ""} ${photo ? "" : styles.withoutPhoto}`}>
      {expanded && photo ? <div className={styles.photo} aria-hidden="true"><Image src={photo} alt="" fill unoptimized priority style={imagePosition} /></div> : null}
      <div className={styles.layout}>
        {expanded ? <div className={styles.content}>
          <div className={styles.intro}>
            <a className={`${styles.avatar} ${visibleLogo ? "" : styles.defaultAvatar}`} href={base} aria-label={`${portal.name} — ${t("portal.navigation.backToOverview")}`}>
              {visibleLogo ? <Image src={visibleLogo} alt={portal.name} width={56} height={56} unoptimized priority onError={() => setFailedLogo(visibleLogo)} />
                : <span aria-hidden="true">{initials(portal.name)}</span>}
            </a>
            <div className={styles.copy}>
              <h1>{portal.name}</h1>
              <p className={styles.description}>{description}</p>
              {presentation?.address || map ? <div className={styles.location}>
                {presentation?.address ? <span>{presentation.address}</span> : null}
                {map ? <a href={map} target="_blank" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="m12 3 9 9-9 9-9-9 9-9Z" /><path d="M8 15v-4h7m-3-3 3 3-3 3" /></svg>{t("portal.home.showOnMap")}</a> : null}
              </div> : null}
            </div>
          </div>
          {hasReviews || portal.reviewSummary ? <div className={styles.reviews}><PortalReviewSummary portalSlug={portal.slug} publicSummary={portal.reviewSummary} /></div> : null}
          {hasBooking || hasEvents ? <a className={styles.cta} href={`${base}${hasBooking ? "#services" : "#events"}`}>
            {t(hasBooking ? "portal.home.reserveAppointment" : "portal.home.browseEvents")}<span aria-hidden="true">→</span>
          </a> : null}
        </div> : null}
      </div>
    </div>
  </header>;
}
